import React from 'react';
import { Box, Fab, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { WorkQueue } from '../../../data/models/work-queue.model';
import { MultiSelectEditCell } from '../../forms/MultiSelectEditCell';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as uiSlice from '../../../data/ui.slice';
import * as monitoringSlice from '../../../data/monitoring.slice';
import isEqual from 'lodash/isEqual';
import { UserRole } from '../../../data/models/user-role.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'WorkQueuesTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledPaper = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    flexGrow: 1,
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    width: '100%',
    backgroundColor: theme.palette.background.default,
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
}));

interface WorkQueuesTableProps {
  userRoles: UserRole[];
  workQueues: WorkQueue[];
  loading: boolean;
}

export default function WorkQueuesTable({
  userRoles,
  workQueues,
  loading,
}: WorkQueuesTableProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isSuperReaderUser = useIsSuperReaderUser();

  const [rows, setRows] = React.useState<WorkQueue[]>();
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingNewItem, setIsEditingNewItem] = React.useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = React.useState('');
  const [deleteQueue, setDeleteQueue] = React.useState<WorkQueue>();

  React.useEffect(() => {
    if (workQueues) {
      setRows(workQueues);
    }
  }, [workQueues]);

  const getUserRoleName = (userRoleID: number) => {
    const userRole = userRoles.find((a) => a.userRoleID === userRoleID);
    return userRole ? userRole.userRoleName : null;
  };

  const formatUserRoles = (values: any[]) => {
    const names = values?.map((v) => getUserRoleName(v)) || [];
    return names.filter((n) => !!n).join(', ');
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('workQueue.queueName'),
      width: 300,
      sortable: false,
      editable: true,
    },
    {
      field: 'assignableSupervisorRoleIDs',
      headerName: t('workQueue.supervisors'),
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      valueOptions: userRoles,
      getOptionValue: (value: any) => value.userRoleID,
      getOptionLabel: (value: any) => value.userRoleName,
      valueFormatter: ({ value }) => formatUserRoles(value),
      renderEditCell: MultiSelectEditCell,
      filterOperators: [
        {
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') {
              return null;
            }
            return ({ value }) => {
              return value.some(
                (cellValue: any) => cellValue === filterItem.value,
              );
            };
          },
          // InputComponent: CustomFilterInputSingleSelect,
        },
      ],
    },
    {
      field: 'assignableUserRoleIDs',
      headerName: t('workQueue.users'),
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      valueOptions: userRoles,
      getOptionValue: (value: any) => value.userRoleID,
      getOptionLabel: (value: any) => value.userRoleName,
      valueFormatter: ({ value }) => formatUserRoles(value),
      renderEditCell: MultiSelectEditCell,
      filterOperators: [
        {
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') {
              return null;
            }
            return ({ value }) => {
              return value.some(
                (cellValue: any) => cellValue === filterItem.value,
              );
            };
          },
          // InputComponent: CustomFilterInputSingleSelect,
        },
      ],
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }: { id: GridRowId }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const workQueue = workQueues.find((q) => q.workQueueUUID === id);

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        const isWorkQueueActive = workQueue?.state === 'active';
        const switchLabel = isWorkQueueActive
          ? t('common.deactivate')
          : t('common.activate');

        const actions = [
          <GridActionsCellItem
            icon={
              <Tooltip title="Edit">
                <EditOutlinedIcon fontSize="medium" />
              </Tooltip>
            }
            label="Edit"
            className="textPrimary"
            disabled={isEditing}
            onClick={handleEditClick(id)}
            color="primary"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Delete">
                <DeleteForeverIcon fontSize="medium" />
              </Tooltip>
            }
            label="Delete"
            className="textPrimary"
            disabled={isEditing || isWorkQueueActive}
            onClick={handleDeleteClick(id)}
            color="error"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title={switchLabel}>
                <Switch checked={isWorkQueueActive} disabled={isEditing} />
              </Tooltip>
            }
            label={switchLabel}
            className="textPrimary"
            disabled={isEditing}
            onClick={toggleState(id)}
            color={isWorkQueueActive ? 'primary' : 'info'}
          />,
        ];

        return !isSuperReaderUser ? actions : [];
      },
    },
  ];

  const handleEditClick = React.useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
      setIsEditing(true);
      setIsEditingNewItem(false);
    },
    [rowModesModel],
  );

  const handleDeleteClick = (id: GridRowId) => () => {
    const workQueue = workQueues.find((q) => q.workQueueUUID === id);
    if (workQueue) {
      const msg = t('workQueue.confirmDeleteMessage', { name: workQueue.name });
      setConfirmDeleteMessage(msg);
      setDeleteQueue(workQueue);
      setConfirmDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (deleteQueue) {
      dispatch(monitoringSlice.deleteWorkQueueAsync(deleteQueue));
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const handleActivateClick = (id: GridRowId) => () => {
    const workQueue = workQueues.find((q) => q.workQueueUUID === id);
    if (workQueue) {
      dispatch(monitoringSlice.activateWorkQueueAsync(workQueue));
    }
  };

  const toggleState = (id: GridRowId) => () => {
    const workQueue = workQueues.find((q) => q.workQueueUUID === id);
    if (workQueue) {
      if (workQueue.state === 'active') {
        dispatch(monitoringSlice.deactivateWorkQueueAsync(workQueue));
      } else {
        dispatch(monitoringSlice.activateWorkQueueAsync(workQueue));
      }
    }
  };

  const handleSaveClick = React.useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View },
      });
      setIsEditing(false);
    },
    [rowModesModel],
  );

  const handleCancelClick = React.useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
      if (rows) {
        setRows(rows.filter((row) => !!row.state));
      }

      setIsEditing(false);
    },
    [rowModesModel],
  );

  const handleRowClick = (params: GridRowParams) => {
    const id = encodeURIComponent(params.id);
    navigate(`/workQueue/${id}`);
  };

  const processRowUpdate = (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel,
  ) => {
    const updatedRow = { ...newRow };
    if (
      !newRow.name.trim() ||
      !newRow.assignableSupervisorRoleIDs.length ||
      !newRow.assignableUserRoleIDs.length
    ) {
      dispatch(uiSlice.setErrorMessage('workQueue.lookupRequiredFields'));
      setTimeout(() => {
        setRowModesModel({
          ...rowModesModel,
          [oldRow.workQueueUUID]: {
            mode: GridRowModes.Edit,
          },
        });
        setIsEditing(true);
      }, 10);
      return updatedRow;
    }

    const changed = !isEqual(newRow, oldRow);
    if (changed) {
      const workQueue = {
        workQueueUUID: newRow.state ? newRow.workQueueUUID : undefined,
        name: newRow.name,
        assignableSupervisorRoleIDs: newRow.assignableSupervisorRoleIDs,
        assignableUserRoleIDs: newRow.assignableUserRoleIDs,
      };
      if (!newRow.state) {
        dispatch(monitoringSlice.createWorkQueueAsync(workQueue));
        setRows(rows!.filter((row) => !!row.state));
      } else {
        dispatch(monitoringSlice.updateWorkQueueAsync(workQueue));
      }
    }
    return updatedRow;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const onAddQueue = () => {
    const id = crypto.randomUUID();
    const newComponent: WorkQueue = {
      workQueueUUID: id,
      name: '',
      assignableSupervisorRoleIDs: [],
      assignableUserRoleIDs: [],
    };
    setRows((oldRows) => [...(oldRows || []), newComponent]);
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit },
    });
    setIsEditing(true);
    setIsEditingNewItem(true);
  };

  return (
    <StyledPaper className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.workQueueUUID}
        rows={rows || []}
        columns={columns}
        pagination={false}
        autoHeight={true}
        loading={loading}
        sortingMode="server"
        className={classes.grid}
        checkboxSelection={false}
        disableColumnMenu={true}
        disableColumnSelector={true}
        onRowClick={handleRowClick}
        hideFooter={true}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
      />
      {!isSuperReaderUser && (
        <Tooltip title={t('workQueue.addQueue')}>
          <span>
            <Fab
              data-testid="add-queue"
              disabled={isEditing}
              onClick={onAddQueue}
              color="primary">
              <AddCircleOutlineOutlinedIcon />
            </Fab>
          </span>
        </Tooltip>
      )}
      <ConfirmationDialog
        title={t('workQueue.confirmDeleteTitle')}
        message={confirmDeleteMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledPaper>
  );
}
